import * as model from '@owl-nest/models'
import * as api from '@owl-nest/api-client/latest'

type OnboardingConfig = {
  project: api.Project
  namespace: string
  setOnboardingSteps: (
    nextSteps: model.project.ProjectForOwner['onboarding_steps'],
    extra?: model.project.UpdateProject,
  ) => Promise<void>
}

// This is not actually a real hook: it does not use any primitive react hook
// and does not use custom hooks. But we still use the hook naming scheme
// because this is a hook in spirit.
//
// In a perfect world the `setOnboardingStep` should send a PATCH to the
// project resource (using the `useProject` hook in the
// `@owl-nest/service` package, relying on the `useSharedQuery` hook). But we
// are not in a perfect world, and `useOnboarding` is used in the
// `project` feature that still uses redux.
//
// We could use `useProject` here, but we would get a `project` that is not in
// sync with redux. We could force redux to be in sync with `useProject` with a
// `SYNC` action, but we could still fall out of sync if the redux state changes
// by itself (through an 'old style' action dispatch). This means we would need
// a two-way sync (redux => atom and atom => redux), and that's
// always a bad idea and a pain to maintain
//
// We could create a hook using the `project` reducer and actions (same as with
// `useAuth`), that way we only ever update the `atom`, and in return the
// `atom` updates the redux state (one way sync, yay!), but this means we
// will have two different ways of handling api requests (old school ad-hoc
// reducer vs `useSharedQuery`), and two different stores that we still need to
// sync two-way style.
//
// The solution is to extract away state management. The
// `setOnboardingSteps` passed to the hook uses whatever state management it
// chooses, and here we do our magic, and call the state management when done.
//
// Once redux is removed, it should be possible to transform this "pseudo-hook"
// into a real hook, relying on the one and only `useProject` hook instead.
export function useOnboardingStep({
  project,
  namespace,
  setOnboardingSteps,
}: OnboardingConfig): (name: string, value: any, extra?: model.project.UpdateProject) => Promise<void> {
  const currentSteps = project?.onboarding_steps?.[namespace] || {}

  return setOnboardingStep

  async function setOnboardingStep(name: string, value: any, extra?: model.project.UpdateProject): Promise<void> {
    if (currentSteps[name] === value) {
      return
    }

    const nextSteps = {
      ...(project.onboarding_steps || {}),
      [namespace]: {
        ...(currentSteps || {}),
        [name]: value,
      },
    }

    return setOnboardingSteps(nextSteps, extra)
  }
}
